import { useEffect, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Maximize2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { loadAriaResults, type AriaResults, type RadarAxis, type DimScore, type LossSeries } from './ariaResults.data';
import './aria-results.css';

// ── Fingerprint radar ───────────────────────────────────────────────────────
function FingerprintRadar({ axes, label }: { axes: RadarAxis[]; label: (k: string) => string }) {
  // viewBox enlarged to 320×280 so 6-axis labels (incl. long ones like
  // "Zitat-Abdeckung") never get clipped at the edges. R reduced to 75 and
  // label radius to 100 to keep everything comfortably inside the canvas.
  const cx = 160, cy = 130, R = 75;
  const N = axes.length;
  const angleAt = (i: number) => (-90 + (i * 360) / N) * (Math.PI / 180);
  const pointAt = (i: number, v: number) => {
    const r = (R * Math.max(0, Math.min(100, v))) / 100;
    return [cx + r * Math.cos(angleAt(i)), cy + r * Math.sin(angleAt(i))] as const;
  };
  const poly = (key: 'base' | 'aria') =>
    axes.map((a, i) => pointAt(i, a[key]).join(',')).join(' ');

  const rings = [25, 50, 75, 100];

  return (
    <svg viewBox="0 0 320 280" className="bnk-aria__radar-svg" role="img" aria-label="Model fingerprint radar">
      {/* rings */}
      {rings.map((rv) => (
        <circle key={rv} cx={cx} cy={cy} r={(R * rv) / 100} className="bnk-aria__radar-ring" fill="none" />
      ))}
      {/* axes + labels */}
      {axes.map((a, i) => {
        const [ex, ey] = pointAt(i, 100);
        // Label positioned at a fixed offset outside the outer ring (R + 18),
        // independent of the value-clamped pointAt() function.
        const lr = R + 18;
        const lx = cx + lr * Math.cos(angleAt(i));
        const ly = cy + lr * Math.sin(angleAt(i));
        const anchor = Math.abs(lx - cx) < 6 ? 'middle' : lx > cx ? 'start' : 'end';
        return (
          <g key={a.key}>
            <line x1={cx} y1={cy} x2={ex} y2={ey} className="bnk-aria__radar-spoke" />
            <text x={lx} y={ly} textAnchor={anchor} dominantBaseline="middle" className="bnk-aria__radar-label">
              {label(a.key)}
            </text>
          </g>
        );
      })}
      {/* base polygon */}
      <polygon points={poly('base')} className="bnk-aria__radar-base" />
      {/* aria polygon */}
      <polygon points={poly('aria')} className="bnk-aria__radar-aria" />
      {/* aria vertices */}
      {axes.map((a, i) => {
        const [x, y] = pointAt(i, a.aria);
        return <circle key={a.key} cx={x} cy={y} r={3} className="bnk-aria__radar-aria-dot" />;
      })}
    </svg>
  );
}

// ── Scorecard grouped bars ──────────────────────────────────────────────────
function ScorecardBars({ data, label }: { data: DimScore[]; label: (k: string) => string }) {
  const W = 320, H = 210, base = 158, top = 24, barW = 30, gap = 14;
  const groupW = barW * 2 + gap;
  const slot = W / data.length;
  const yOf = (v: number) => base - ((base - top) * v) / 100;

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="bnk-aria__radar-svg" role="img" aria-label="Scorecard bars">
      {/* gridlines */}
      {[0, 25, 50, 75, 100].map((g) => (
        <g key={g}>
          <line x1={28} y1={yOf(g)} x2={W} y2={yOf(g)} className="bnk-aria__bar-grid" />
          <text x={22} y={yOf(g) + 3} textAnchor="end" className="bnk-aria__bar-axis">{g}</text>
        </g>
      ))}
      {data.map((d, i) => {
        const gx = i * slot + (slot - groupW) / 2 + 6;
        return (
          <g key={d.key}>
            <rect x={gx} y={yOf(d.base)} width={barW} height={base - yOf(d.base)} rx={3} className="bnk-aria__bar-base" />
            <rect x={gx + barW + gap} y={yOf(d.aria)} width={barW} height={base - yOf(d.aria)} rx={3} className="bnk-aria__bar-aria" />
            <text x={gx + barW / 2} y={yOf(d.base) - 5} textAnchor="middle" className="bnk-aria__bar-val base">{d.base}</text>
            <text x={gx + barW + gap + barW / 2} y={yOf(d.aria) - 5} textAnchor="middle" className="bnk-aria__bar-val aria">{d.aria}</text>
            <text x={gx + groupW / 2} y={base + 18} textAnchor="middle" className="bnk-aria__bar-cat">{label(d.key)}</text>
          </g>
        );
      })}
    </svg>
  );
}

// ── Training-loss line chart ────────────────────────────────────────────────
function LossChart({ loss, rawLabel, smoothLabel }: { loss: LossSeries; rawLabel: string; smoothLabel: string }) {
  const W = 320, H = 200, padL = 34, padR = 10, padT = 14, padB = 26;
  const pts = loss.points;
  if (pts.length === 0) return null;
  const maxStep = pts[pts.length - 1].step;
  const maxLoss = Math.max(...pts.map((p) => p.loss), 1);
  const xOf = (s: number) => padL + ((W - padL - padR) * (s - 1)) / Math.max(1, maxStep - 1);
  const yOf = (v: number) => padT + (H - padT - padB) * (1 - v / maxLoss);
  const path = (key: 'loss' | 'smooth') =>
    pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${xOf(p.step).toFixed(1)},${yOf(p[key]).toFixed(1)}`).join(' ');

  const yTicks = [0, maxLoss / 2, maxLoss];

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="bnk-aria__radar-svg" role="img" aria-label="Training loss curve">
      {yTicks.map((v, i) => (
        <g key={i}>
          <line x1={padL} y1={yOf(v)} x2={W - padR} y2={yOf(v)} className="bnk-aria__bar-grid" />
          <text x={padL - 5} y={yOf(v) + 3} textAnchor="end" className="bnk-aria__bar-axis">{v.toFixed(1)}</text>
        </g>
      ))}
      <path d={path('loss')} className="bnk-aria__loss-raw" fill="none" />
      <path d={path('smooth')} className="bnk-aria__loss-smooth" fill="none" />
      {/* x axis ticks: start + end */}
      <text x={padL} y={H - 8} textAnchor="start" className="bnk-aria__bar-axis">1</text>
      <text x={W - padR} y={H - 8} textAnchor="end" className="bnk-aria__bar-axis">{maxStep}</text>
      {/* legend */}
      <g transform={`translate(${padL + 6}, ${padT + 4})`}>
        <line x1={0} y1={0} x2={16} y2={0} className="bnk-aria__loss-raw" />
        <text x={20} y={3} className="bnk-aria__loss-legend">{rawLabel}</text>
        <line x1={0} y1={14} x2={16} y2={14} className="bnk-aria__loss-smooth" />
        <text x={20} y={17} className="bnk-aria__loss-legend">{smoothLabel}</text>
      </g>
    </svg>
  );
}

// ── Eval-grid dots ──────────────────────────────────────────────────────────
function DotRow({ cases }: { cases: boolean[] }) {
  return (
    <div className="bnk-aria__dotrow">
      {cases.map((ok, i) => (
        <span key={i} className={`bnk-aria__dot ${ok ? 'p' : 'f'}`} />
      ))}
    </div>
  );
}

// ── Main section ────────────────────────────────────────────────────────────
export function AriaResultsSection() {
  const { t } = useTranslation('home');
  const a = (k: string) => t(`ariaResults.${k}`);
  const dimLabel = (k: string) => t(`ariaResults.dims.${k}`);
  const [data, setData] = useState<AriaResults | null>(null);
  const [failed, setFailed] = useState(false);
  const [activeChart, setActiveChart] = useState<'fingerprint' | 'scorecard' | 'loss' | null>(null);

  const closeModal = useCallback(() => setActiveChart(null), []);

  useEffect(() => {
    let alive = true;
    loadAriaResults()
      .then((r) => { if (alive) setData(r); })
      .catch((e) => { console.error(e); if (alive) setFailed(true); });
    return () => { alive = false; };
  }, []);

  // Close modal on Escape
  useEffect(() => {
    if (!activeChart) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeModal();
    };
    window.addEventListener('keydown', handler);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', handler);
      document.body.style.overflow = '';
    };
  }, [activeChart, closeModal]);

  // Don't render the whole section if the data is unavailable.
  if (failed) return null;

  return (
    <section id="aria-results" className="bnk-aria">
      <div className="bnk-aria__wrap">
        {/* Knockout stat cards (base → ARIA) */}
        <div className="bnk-aria__knockouts">
          {(data?.scorecard ?? [{ key: 'identity' }, { key: 'security' }, { key: 'grounding' }] as DimScore[]).map((d) => (
            <motion.div
              key={d.key}
              className="bnk-aria__ko"
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.5 }}
            >
              <div className="bnk-aria__ko-label">{dimLabel(d.key)}</div>
              <div className="bnk-aria__ko-numbers">
                <span className="bnk-aria__ko-num bad">{data ? `${d.base}%` : '—'}</span>
                <span className="bnk-aria__ko-arrow">→</span>
                <span className="bnk-aria__ko-num good">{data ? `${d.aria}%` : '—'}</span>
              </div>
              <div className="bnk-aria__ko-desc">{a(`dimsDesc.${d.key}`)}</div>
            </motion.div>
          ))}
        </div>

        {/* legend */}
        <div className="bnk-aria__legend">
          <span><span className="bnk-aria__legend-dot bnk-aria__legend-dot--base" />{a('legend.base')}</span>
          <span><span className="bnk-aria__legend-dot bnk-aria__legend-dot--aria" />{a('legend.aria')}</span>
        </div>

        {/* Three diagrams: fingerprint · scorecard · loss */}
        <div className="bnk-aria__diagrams">
          <div className="bnk-aria__panel" onClick={() => setActiveChart('fingerprint')} role="button" tabIndex={0} onKeyDown={(e) => e.key === 'Enter' && setActiveChart('fingerprint')}>
            <h3 className="bnk-aria__panel-heading"><span className="dot" />{a('charts.fingerprint.title')}</h3>
            <p className="bnk-aria__diagram-sub">{a('charts.fingerprint.sub')}</p>
            {data && <FingerprintRadar axes={data.fingerprint} label={dimLabel} />}
            <span className="bnk-aria__click-hint"><Maximize2 size={12} />{a('modal.clickHint')}</span>
          </div>

          <div className="bnk-aria__panel" onClick={() => setActiveChart('scorecard')} role="button" tabIndex={0} onKeyDown={(e) => e.key === 'Enter' && setActiveChart('scorecard')}>
            <h3 className="bnk-aria__panel-heading"><span className="dot" />{a('charts.scorecard.title')}</h3>
            <p className="bnk-aria__diagram-sub">{a('charts.scorecard.sub')}</p>
            {data && <ScorecardBars data={data.scorecard} label={dimLabel} />}
            <span className="bnk-aria__click-hint"><Maximize2 size={12} />{a('modal.clickHint')}</span>
          </div>

          <div className="bnk-aria__panel" onClick={() => setActiveChart('loss')} role="button" tabIndex={0} onKeyDown={(e) => e.key === 'Enter' && setActiveChart('loss')}>
            <h3 className="bnk-aria__panel-heading"><span className="dot" />{a('charts.loss.title')}</h3>
            <p className="bnk-aria__diagram-sub">{a('charts.loss.sub')}</p>
            {data && (
              <>
                <LossChart loss={data.loss} rawLabel={a('charts.loss.raw')} smoothLabel={a('charts.loss.smoothed')} />
                <p className="bnk-aria__note">{a('charts.loss.finalLoss')}: <strong style={{ color: 'var(--aria-aria)' }}>{data.loss.finalLoss.toFixed(3)}</strong></p>
              </>
            )}
            <span className="bnk-aria__click-hint"><Maximize2 size={12} />{a('modal.clickHint')}</span>
          </div>
        </div>

        {/* Eval grid (slide 9, without Diagramme & Profil) */}
        <div className="bnk-aria__panel" style={{ marginTop: 16 }}>
          <h3 className="bnk-aria__panel-heading"><span className="dot" />{a('evalGrid.title')}</h3>
          <p className="bnk-aria__diagram-sub">{a('evalGrid.sub')}</p>
          {data?.evalGrid.map((dim) => (
            <div key={dim.key} className="bnk-aria__egrid">
              <div className="bnk-aria__dim-heading">{dimLabel(dim.key)}</div>
              <span className="bnk-aria__egrid-label bad">{a('legend.base')}</span>
              <DotRow cases={dim.base} />
              <span className="bnk-aria__egrid-cnt bad">{dim.basePass}/{dim.total}</span>
              <span className="bnk-aria__egrid-label good">{a('legend.aria')}</span>
              <DotRow cases={dim.aria} />
              <span className="bnk-aria__egrid-cnt good">{dim.ariaPass}/{dim.total}</span>
            </div>
          ))}
        </div>

        <p className="bnk-aria__disclaimer">{a('disclaimer')}</p>
      </div>

      {/* ── Modal ── */}
      <AnimatePresence>
        {activeChart && data && (
          <motion.div
            className="bnk-aria__modal-overlay"
            onClick={closeModal}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            <motion.div
              className="bnk-aria__modal"
              onClick={(e) => e.stopPropagation()}
              initial={{ opacity: 0, scale: 0.85, y: 30 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              transition={{ type: 'spring', damping: 22, stiffness: 280 }}
            >
              <button className="bnk-aria__modal-close" onClick={closeModal} aria-label="Close">
                <X size={18} />
              </button>

              <h2 className="bnk-aria__modal-heading">
                <span className="dot" style={{ width: 10, height: 10, borderRadius: '50%', background: 'linear-gradient(135deg, var(--color-primary), var(--aria-aria))', flexShrink: 0 }} />
                {a(`charts.${activeChart}.title`)}
              </h2>
              <p className="bnk-aria__diagram-sub">{a(`charts.${activeChart}.sub`)}</p>

              <div className="bnk-aria__modal-diagram-wrap">
                {activeChart === 'fingerprint' && <FingerprintRadar axes={data.fingerprint} label={dimLabel} />}
                {activeChart === 'scorecard' && <ScorecardBars data={data.scorecard} label={dimLabel} />}
                {activeChart === 'loss' && (
                  <>
                    <LossChart loss={data.loss} rawLabel={a('charts.loss.raw')} smoothLabel={a('charts.loss.smoothed')} />
                    <p className="bnk-aria__note">{a('charts.loss.finalLoss')}: <strong style={{ color: 'var(--aria-aria)' }}>{data.loss.finalLoss.toFixed(3)}</strong></p>
                  </>
                )}
              </div>

              <div className="bnk-aria__modal-section">
                <h3 className="bnk-aria__modal-section-title">{a(`modal.${activeChart}.whatTitle`)}</h3>
                <p className="bnk-aria__modal-section-text">{a(`modal.${activeChart}.whatText`)}</p>
              </div>

              <div className="bnk-aria__modal-section">
                <h3 className="bnk-aria__modal-section-title">{a(`modal.${activeChart}.valuesTitle`)}</h3>
                <p className="bnk-aria__modal-section-text">{a(`modal.${activeChart}.valuesText`)}</p>
              </div>

              <div className="bnk-aria__modal-section">
                <h3 className="bnk-aria__modal-section-title">{a(`modal.${activeChart}.impactTitle`)}</h3>
                <p className="bnk-aria__modal-section-text">{a(`modal.${activeChart}.impactText`)}</p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
